import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCode, FaMobileAlt, FaDraftingCompass, FaRocket, FaShieldAlt, FaChartLine } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useSEO, { SITE_URL } from '../utils/useSEO';
import './ServiceLanding.css';

const serviceData = {
    'web-development-vadodara': {
        title:       'Web Development Company in Vadodara',
        subtitle:    'High-Performance Web Solutions',
        description: 'We build scalable, secure, and lightning-fast websites and web applications tailored to your business needs in Vadodara and beyond.',
        features: [
            { icon: <FaCode />,      title: 'Custom Web Apps',    desc: 'Tailor-made solutions using React, Node.js, and modern tech stacks.' },
            { icon: <FaRocket />,    title: 'Performance First',  desc: 'Optimised for speed, Core Web Vitals, SEO, and user experience.' },
            { icon: <FaShieldAlt />, title: 'Secure & Scalable',  desc: 'Enterprise-grade security and architecture that grows with you.' }
        ],
        metaTitle:       'Web Development Company in Vadodara | Nimai Solutions',
        metaDescription: 'Looking for a web development company in Vadodara? Nimai Solutions builds fast, secure, SEO-optimised websites and React web applications for businesses across Gujarat.',
        keywords:        'web development company Vadodara, website development Baroda, React developer Gujarat, custom website Vadodara, web design company Gujarat',
        serviceType:     'Web Development',
    },
    'flutter-app-development': {
        title:       'Flutter App Development Services',
        subtitle:    'Cross-Platform Excellence',
        description: 'Leverage the power of Flutter to build beautiful, natively compiled applications for mobile, web, and desktop from a single codebase.',
        features: [
            { icon: <FaMobileAlt />,  title: 'Single Codebase',      desc: 'Reach iOS and Android users simultaneously with high-quality apps.' },
            { icon: <FaRocket />,     title: 'Fast Development',      desc: 'Quick turnaround times with Hot Reload and pre-built widgets.' },
            { icon: <FaChartLine />,  title: 'Native Performance',    desc: 'Smooth animations and high performance that feels truly native.' }
        ],
        metaTitle:       'Flutter App Development Services India | Nimai Solutions',
        metaDescription: 'Expert Flutter app development services in India. Build beautiful, high-performance cross-platform mobile apps for iOS and Android from a single codebase.',
        keywords:        'Flutter app development India, Flutter developer Vadodara, cross-platform app development Gujarat, Flutter iOS Android app',
        serviceType:     'Flutter App Development',
    },
    'mobile-app-development': {
        title:       'Mobile App Development Company',
        subtitle:    'Innovative Mobile Solutions',
        description: 'From concept to deployment, we create engaging mobile experiences that connect your brand with users on their favourite devices.',
        features: [
            { icon: <FaMobileAlt />,     title: 'iOS & Android',       desc: 'Deep expertise in both major platforms for maximum reach.' },
            { icon: <FaDraftingCompass />, title: 'User-Centric UI',   desc: 'Interfaces designed for intuitive navigation and high engagement.' },
            { icon: <FaRocket />,         title: 'App Store Success',  desc: 'Complete support for App Store and Play Store optimisation.' }
        ],
        metaTitle:       'Mobile App Development Company Vadodara | iOS & Android | Nimai Solutions',
        metaDescription: 'Professional mobile app development company in Vadodara. We build custom iOS and Android applications that drive business growth and delight users.',
        keywords:        'mobile app development Vadodara, iOS app developer Gujarat, Android app development India, app development company Vadodara',
        serviceType:     'Mobile App Development',
    },
    'ui-ux-design-services': {
        title:       'UI/UX Design Services in Vadodara',
        subtitle:    'Human-Centric Design',
        description: 'We create visually stunning and highly functional designs that prioritise user experience and deliver measurable business outcomes.',
        features: [
            { icon: <FaDraftingCompass />, title: 'Interface Design',     desc: 'Modern, clean, and brand-aligned visual interfaces.' },
            { icon: <FaChartLine />,       title: 'Experience Strategy',  desc: 'Data-driven design decisions based on user behaviour and goals.' },
            { icon: <FaRocket />,          title: 'Prototyping',          desc: 'Interactive prototypes to visualise and test the flow before development.' }
        ],
        metaTitle:       'UI/UX Design Services Vadodara | User Experience Design | Nimai Solutions',
        metaDescription: 'Premium UI/UX design services in Vadodara, Gujarat. We create intuitive, accessible, and engaging digital experiences that users love and businesses trust.',
        keywords:        'UI UX design services Vadodara, user experience design Gujarat, app UI design India, website UX designer Vadodara',
        serviceType:     'UI/UX Design',
    },
};

const buildServiceSchema = (serviceId, data) => ({
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "Service",
            "@id": `${SITE_URL}/${serviceId}#service`,
            "name": data.title,
            "serviceType": data.serviceType,
            "description": data.description,
            "provider": {
                "@type": "Organization",
                "@id": `${SITE_URL}/#business`,
                "name": "Nimai Solutions",
                "url": SITE_URL,
            },
            "areaServed": [
                { "@type": "City",    "name": "Vadodara" },
                { "@type": "State",   "name": "Gujarat" },
                { "@type": "Country", "name": "India" }
            ],
            "url": `${SITE_URL}/${serviceId}`,
        },
        {
            "@type": "WebPage",
            "@id": `${SITE_URL}/${serviceId}#webpage`,
            "url": `${SITE_URL}/${serviceId}`,
            "name": data.metaTitle,
            "description": data.metaDescription,
            "isPartOf": { "@id": `${SITE_URL}/#website` },
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home",          "item": SITE_URL },
                    { "@type": "ListItem", "position": 2, "name": data.serviceType, "item": `${SITE_URL}/${serviceId}` }
                ]
            }
        }
    ]
});

const ServiceLanding = () => {
    const { serviceId } = useParams();
    const navigate      = useNavigate();
    const data          = serviceData[serviceId];

    // Redirect unknown service slugs to 404
    if (!data) {
        navigate('/404', { replace: true });
        return null;
    }

    // Per-service SEO — replaces the old document.title / querySelector approach
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useSEO({
        title:     data.metaTitle,
        description: data.metaDescription,
        canonical: `${SITE_URL}/${serviceId}`,
        keywords:  data.keywords,
        schema:    buildServiceSchema(serviceId, data),
    });

    return (
        <div className="service-landing-container">
            <Navbar />

            <section className="service-landing-hero">
                <motion.span
                    className="service-badge"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {data.subtitle}
                </motion.span>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    {data.title.split(' ').map((word, i) => (
                        <span key={i} className={i >= data.title.split(' ').length - 2 ? 'highlight' : ''}>
                            {word}{' '}
                        </span>
                    ))}
                </motion.h1>
                <motion.p
                    className="service-landing-desc"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    {data.description}
                </motion.p>
                <motion.button
                    className="btn-primary"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    onClick={() => navigate('/contact')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Get a Free Consultation
                </motion.button>
            </section>

            <section className="service-features-section">
                <div className="features-grid">
                    {data.features.map((feature, index) => (
                        <motion.div
                            key={index}
                            className="feature-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <div className="feature-icon">{feature.icon}</div>
                            <h3>{feature.title}</h3>
                            <p>{feature.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section className="service-cta">
                <div className="cta-box">
                    <h2>Ready to Scale Your Business?</h2>
                    <p style={{ color: '#A0AEC0', marginBottom: '30px' }}>
                        Let's build something amazing together. Our team is ready to help you achieve your digital goals.
                    </p>
                    <button className="btn-primary" onClick={() => navigate('/contact')}>
                        Contact Us Today
                    </button>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ServiceLanding;
